<template>
  <Card body-classes="p-0" class="mb-5 shadow-sm border-0">
    <template v-slot:header>
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <i class="bi bi-cart-check me-2 text-primary"></i>
          <span class="fw-medium">{{ title }} Products</span>
          <Badge pill type="primary" class="ms-2">{{detailProducts.total || products.length}}</Badge>
        </div>
        <div class="d-flex gap-2">
          <button class="btn btn-outline-dark" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
          <button v-if="products.length > 0" class="btn btn-outline-primary" @click="exportToPDF">
            <i class="bi bi-file-pdf me-1"></i> Export PDF
          </button>
          <slot name="header-actions"></slot>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center py-5">
      <Spinner spinner-class="mb-1" />
      <p class="text-muted mt-2">Loading products...</p>
    </div>

    <div v-if="!loading">
      <div class="d-flex justify-content-between align-items-center px-3 py-2 bg-light flex-wrap gap-2">
        <div class="text-muted d-flex align-items-center gap-2">
          <span class="fw-medium">Page {{ currentPage }} of {{ totalPages }}</span>
          <span class="text-secondary">•</span>
          <span>Showing <span class="badge bg-info">{{ filteredProducts.length }}</span> of <span class="badge bg-primary">{{ detailProducts.total || products.length }}</span> items</span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-nowrap">
          <div v-if="detailProducts.total > products.length" class="d-flex align-items-center gap-2 flex-shrink-0">
            <label class="text-muted mb-0">Items per page:</label>
            <select class="form-select form-select-sm" style="width: auto" :value="currentPerPage" @change="$emit('update:per-page', parseInt($event.target.value))">
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="250">250</option>
              <option value="500">500</option>
              <option value="1000">1000</option>
              <option value="2000">2000</option>
            </select>
          </div>
          <div class="input-group input-group-sm">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
            <input
              type="text"
              class="form-control"
              placeholder="Search products..."
              :value="currentSearch"
              @input="onSearchInput"
              style="min-width: 150px;"
            >
            <button v-if="currentSearch" class="btn btn-outline-secondary" type="button" @click="$emit('update:search', '')">
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="table-responsive">
      <table class="table table-hover align-middle mb-0">
        <thead>
        <tr>
          <th>SKU</th>
          <th>CODE</th>
          <th>NAME</th>
          <th v-if="!hideCommonFields">QTY</th>
          <th v-if="!hideCommonFields && !hidePricingFields">PRICE</th>
          <th v-if="additionalFields.length > 0" v-for="field in additionalFields">
            {{ field.label }}
          </th>
          <th v-if="hideCommonFields && additionalFields.length > 0">
            DIFF.
          </th>
          <th v-if="!hideCommonFields && !hidePricingFields">TOTAL</th>
          <th v-if="hasSupplierData">SUPPLIER</th>
          <th>COMMENT</th>
          <th v-if="main.status === 'pending' || main.status === 'draft'" class="text-end">ACTIONS</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="products.length === 0">
          <td colspan="8" class="text-center py-4 text-muted">
            <i class="bi bi-inbox fs-4 d-block mb-2"></i>
            No products to display
          </td>
        </tr>
        <tr v-for="(product, index) in filteredProducts" :key="product.id" :class="{'bg-light': index % 2 === 0}">
          <td>{{ product.product?.sku || '-' }}</td>
          <td>{{ product.product?.code || '-' }}</td>
          <td>{{ product.product?.name || '-' }}</td>
          <td v-if="!hideCommonFields">{{ product.quantity }} {{ product.product?.unit?.name || '' }}</td>
          <td v-if="!hideCommonFields && !hidePricingFields">{{ formatNumber((product.price || product.sell || product.product?.sell)) }}</td>
          <td v-if="additionalFields.length > 0" v-for="field in additionalFields">
            {{ field.format ? formatNumber(product[field.name]) : product[field.name] }}
          </td>
          <td v-if="hideCommonFields && additionalFields.length > 0">
            <span :class="`badge bg-${stockDiff(product).status}`">
              {{ stockDiff(product).diff }} {{ product.product.unit.name }}
            </span>
          </td>
          <td v-if="!hideCommonFields && !hidePricingFields">{{ formatNumber((product.price || product.sell || product.product?.sell) * product.quantity) }}</td>
          <td v-if="hasSupplierData">{{ getSupplierName(product) }}</td>
          <td>{{ product.comment || '-' }}</td>
          <td v-if="main.status === 'pending' || main.status === 'draft'" class="text-end">
            <button
                class="btn btn-sm btn-outline-primary me-2"
                @click="$emit('edit-product', product)"
                title="Edit product"
            >
              <i class="bi bi-pencil"></i>
            </button>
            <button
                class="btn btn-sm btn-outline-danger"
                @click="$emit('delete-product', product.id)"
                title="Remove product"
            >
              <i class="bi bi-trash"></i>
            </button>
          </td>
        </tr>
        <tr v-if="!hidePricingFields" class="fw-bold total-row">
          <td colspan="5" class="text-end">Total:</td>
          <td>{{ formatNumber(calculateTotal) }}</td>
          <td :colspan="hasSupplierData ? 2 : 2"></td>
        </tr>
        </tbody>
      </table>
      </div>

      <!-- Pagination Controls -->
      <div v-if="totalPages > 1" class="d-flex justify-content-center align-items-center py-3 border-top">
        <Pagination
          :model-value="currentPage"
          :pages="totalPages"
          :threshold="2"
          @update:modelValue="$emit('update:page', $event)"
        >
          <template #previous>Prev</template>
          <template #next>Next</template>
        </Pagination>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="card-footer bg-white d-flex justify-content-center py-3 gap-3">
      <button class="btn btn-outline-dark" @click="$router.go(-1)">
        <i class="bi bi-arrow-left me-1"></i> Back
      </button>
      <button v-if="products.length > 0" class="btn btn-outline-primary" @click="exportToPDF">
        <i class="bi bi-file-pdf me-1"></i> Export PDF
      </button>
      <slot name="footer-actions"></slot>
    </div>
  </Card>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import formatDate from "@/utils/formatDate";
import confirmDialog  from "@/utils/confirmDialog";
import exportToPDF from "@/utils/exportToPDF";

export default {
  name: "ProductDetailsPages",

  emits: ['update:page', 'update:per-page', 'update:search', 'edit-product', 'delete-product'],

  props: {
    title: {
      type: String,
      required: true
    },
    loading: {
      type: Boolean,
      default: true,
      required: true
    },
    main: {
      type: Object,
      required: true
    },
    detailProducts: {
      type: [Array, Object],
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    currentPerPage: {
      type: Number,
      default: 20
    },
    currentSearch: {
      type: String,
      default: ''
    },
    additionalFields: {
      type: Array,
      default: () => []
    },
    hideCommonFields: {
      type: Boolean,
      default: false
    },
    hidePricingFields: {
      type: Boolean,
      default: false
    },
    pdfTitle: {
      type: String,
      default: ''
    },
    pdfOrgTitle: {
      type: String,
      default: ''
    },
    pdfOrgInfo: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      searchDebounce: null
    }
  },

  methods:{
    formatDate,
    formatNumber,
    confirmDialog,
    onSearchInput(event) {
      const searchValue = event.target.value;
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.$emit('update:search', searchValue);
      }, 400);
    },
    exportToPDF() {
      // Prepare information lines
      const info = [
        `${this.main.store.code} - ${this.main.store.name}`,
        `Created by: ${this.main.user.first_name} ${this.main.user.last_name}`,
        `Created at: ${this.formatDate(this.main.created_at)}`
      ];

      const belowOrgTitle = this.pdfOrgTitle;
      const belowOrgInfo = this.pdfOrgInfo;

      // Prepare table columns
      const tableColumns = ["SKU", "CODE", "NAME"];
      const extraPricingLabels = this.additionalFields.filter(f => f.format).map(f => f.label);
      const commonFields = this.hidePricingFields
        ? ["QTY", ...extraPricingLabels]
        : ["QTY", "PRICE", ...extraPricingLabels, "TOTAL"];
      const stockTakeFields = this.additionalFields.length > 0 ? ["AVAILABLE", "DIFF"] : [];
      !this.hideCommonFields && tableColumns.push(...commonFields);
      this.hideCommonFields && tableColumns.push(...stockTakeFields);
      this.hasSupplierData && tableColumns.push("SUPPLIER");
      const commentsFields = ["COMMENT"];
      tableColumns.push(...commentsFields);

      // Prepare table data (use filtered products for PDF export)
      const tableData = this.filteredProducts.map(product => {
        const row = [
          product.product.sku,
          product.product.code,
          product.product.name
        ];
        
        if (!this.hideCommonFields) {
          row.push(`${product.quantity} ${product.product.unit.name}`);
          if (!this.hidePricingFields) {
            row.push(this.formatNumber((product.price || product.sell || product.product?.sell), false));
            this.additionalFields.filter(f => f.format).forEach(f => {
              row.push(this.formatNumber(product[f.name], false));
            });
            row.push(this.formatNumber((product.price || product.sell || product.product?.sell) * product.quantity, false));
          } else {
            this.additionalFields.filter(f => f.format).forEach(f => {
              row.push(this.formatNumber(product[f.name], false));
            });
          }
        } else if (this.additionalFields.length > 0) {
          row.push(`${product.available} ${product.product.unit.name}`);
          row.push(`${this.stockDiff(product).diff} ${product.product.unit.name}`);
        }

        if (this.hasSupplierData) {
          row.push(this.getSupplierName(product));
        }

        row.push(product.comment || '-');

        return row;
      });

      const pdfTopTitle = this.pdfTitle || this.title.toUpperCase();

      // Call the exportToPDF utility function
      exportToPDF({
        topTitle: pdfTopTitle,
        title: '#' + this.main.code ?? this.title,
        info,
        belowOrgTitle,
        belowOrgInfo,
        tableColumns,
        tableData,
        totalRow: this.hidePricingFields ? undefined : [
          {
            colSpan: 5,
            label: 'SUBTOTAL:',
            value: this.calculateSubTotal
          },
          {
            colSpan: 5,
            label: 'TAX TOTAL:',
            value: this.calculateTaxTotal
          },
          {
            colSpan: 5,
            label: 'GRAND TOTAL:',
            value: this.calculateTotal
          }
        ],
        filename: `${pdfTopTitle}_${this.main.store.code}_${this.formatDate(this.main.created_at)}.pdf`,
        formatNumber: (val) => this.formatNumber(val, false)
      });
    },
    stockDiff(stockTakeProduct) {
      let diff = stockTakeProduct.available - stockTakeProduct.expected
      let status = 'success'
      if (diff < 0) {
        status = 'danger'
      } else if (diff > 0) {
        status = 'warning'
      }
      return {diff, status}
    },
    getSupplierName(product) {
      if (product.supplier) {
        return product.supplier.name;
      } else if (product.product && product.product.supplier) {
        return product.product.supplier.name;
      }
      return '-';
    },
  },

  computed: {
    // Normalize detailProducts to always be an array
    products() {
      return Array.isArray(this.detailProducts)
        ? this.detailProducts
        : (this.detailProducts?.data || []);
    },
    // Filter products based on search term (client-side)
    filteredProducts() {
      if (!this.currentSearch || this.currentSearch.trim() === '') {
        return this.products;
      }
      const searchLower = this.currentSearch.toLowerCase().trim();
      return this.products.filter(product => {
        const p = product.product || product;
        const sku = (p.sku || '').toLowerCase();
        const code = (p.code || '').toLowerCase();
        const name = (p.name || '').toLowerCase();
        return sku.includes(searchLower) ||
               code.includes(searchLower) ||
               name.includes(searchLower);
      });
    },
    totalPages() {
      if (Array.isArray(this.detailProducts)) return 1;
      return this.detailProducts?.last_page || 1;
    },
    calculateSubTotal() {
      return this.filteredProducts.reduce((sum, item) => sum + (item.quantity * (item.price || item.sell || item.product?.sell) - ((item.product.tax_category.rate / 100) * (item.price || item.sell || item.product?.sell) * item.quantity)), 0);
    },
    calculateTaxTotal() {
      return this.filteredProducts.reduce((sum, item) => sum + ((item.product.tax_category.rate / 100) * (item.price || item.sell || item.product?.sell) * item.quantity), 0);
    },
    calculateTotal() {
      return this.filteredProducts.reduce((sum, item) => sum + (item.quantity * (item.price || item.sell || item.product?.sell)), 0);
    },
    hasSupplierData() {
      return this.filteredProducts.some(product =>
        (product.supplier && product.supplier.name) ||
        (product.product && product.product.supplier && product.product.supplier.name)
      );
    }
  },

}
</script>

<style scoped>

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 0.75rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
  font-size: 0.9rem;
}
</style>
